'use client';

import React, { useEffect, useState, useLayoutEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { Page } from '../../types';

interface MoreDropdownProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  open: boolean;
  onClose: () => void;
  pages: Page[];
}

const MoreDropdown: React.FC<MoreDropdownProps> = ({
  containerRef,
  open,
  onClose,
  pages,
}) => {
  const pathname = usePathname();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const currentTab = pathname ? '/' + pathname.split('/')[1] : '/';

  const isPathActive = (path: string): boolean => {
    if (!pathname) return false;
    if (path === '/') {
      return currentTab === '/' || currentTab === '/strategy-center';
    }
    if (path === '/search') {
      return currentTab === '/search';
    }
    // Exact match for specific report paths like /global-reports/123
    if (path.startsWith('/global-reports/')) {
      return pathname === path;
    }
    return pathname.startsWith(path);
  };

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [open, containerRef, onClose]);

  useLayoutEffect(() => {
    if (open && containerRef.current && dropdownRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.offsetHeight || 0;
      setPosition({
        top: rect.top + 4 + rect.height / 2 - dropdownHeight / 2,
        left: rect.right + 5,
      });

      const firstLink = dropdownRef.current.querySelector('a');
      if (firstLink) {
        firstLink.focus();
      }
    }
  }, [open, containerRef]);

  const [expandedSubmenu, setExpandedSubmenu] = useState<string | null>(null);
  const submenuTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleItemClick = () => {
    setExpandedSubmenu(null);
    onClose();
  };

  const handleSubmenuEnter = useCallback((label: string) => {
    if (submenuTimerRef.current) {
      clearTimeout(submenuTimerRef.current);
      submenuTimerRef.current = null;
    }
    setExpandedSubmenu(label);
  }, []);

  const handleSubmenuLeave = useCallback(() => {
    submenuTimerRef.current = setTimeout(() => {
      setExpandedSubmenu(null);
    }, 150);
  }, []);

  useEffect(() => {
    return () => {
      if (submenuTimerRef.current) {
        clearTimeout(submenuTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setExpandedSubmenu(null);
    }
  }, [open]);

  if (!open) return null;

  const itemClass = (isActive: boolean, full?: boolean) =>
    `self-stretch p-2 rounded-lg inline-flex justify-start items-center gap-2 hover:bg-neutral-100 cursor-pointer${full ? ' w-full' : ''} ${isActive ? 'bg-sky-50' : ''}`;

  const renderItemContent = (icon: Page['icon'], label: string, isActive: boolean) => (
    <>
      <Icon
        icon={icon}
        className={`w-4 h-4 ${isActive ? 'text-sky-600' : 'text-text-secondary'}`}
      />
      <span
        className={`text-sm font-medium font-['Inter'] leading-5 whitespace-nowrap ${isActive ? 'text-sky-600' : 'text-text-primary'}`}
      >
        {label}
      </span>
    </>
  );

  return createPortal(
    <div
      ref={dropdownRef}
      style={{ top: position.top, left: position.left }}
      className="min-w-40 fixed bg-white border border-neutral-200 shadow-lg rounded-lg z-[9999] p-1.5"
      data-testid="more-dropdown"
    >
      <div className="flex flex-col justify-start items-start">
        {pages.map((page) => {
          const hasOptions = page.options && page.options.length > 0;
          const targetPath =
            page.path || (page.options && page.options[0]?.path) || '/';

          if (hasOptions) {
            const isSubmenuActive = page.options?.some((opt) =>
              isPathActive(opt.path)
            );
            const isExpanded = expandedSubmenu === page.label;

            return (
              <div
                key={page.label}
                className="w-full relative"
                onMouseEnter={() => handleSubmenuEnter(page.label)}
                onMouseLeave={handleSubmenuLeave}
              >
                <div
                  className={itemClass(!!isSubmenuActive, true)}
                  role="button"
                  tabIndex={0}
                  aria-haspopup="true"
                  aria-expanded={isExpanded}
                  onClick={() =>
                    setExpandedSubmenu(isExpanded ? null : page.label)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedSubmenu(isExpanded ? null : page.label);
                    }
                  }}
                  data-testid={`more-dropdown-item-${page.label}`}
                >
                  {renderItemContent(
                    page.icon,
                    page.label,
                    !!isSubmenuActive
                  )}
                  <Icon
                    icon={faChevronRight}
                    className={`w-3 h-3 ml-auto ${isSubmenuActive ? 'text-sky-600' : 'text-text-secondary'}`}
                  />
                </div>
                {isExpanded && (
                  <div
                    className="absolute left-full top-0 ml-1 min-w-40 bg-white border border-neutral-200 shadow-lg rounded-lg z-[10000] p-1.5"
                    data-testid={`more-submenu-${page.label}`}
                    onMouseEnter={() => handleSubmenuEnter(page.label)}
                    onMouseLeave={handleSubmenuLeave}
                  >
                    {page.options?.map((option) => {
                      const isActive = isPathActive(option.path);
                      return (
                        <Link
                          key={option.path}
                          href={option.path}
                          className={itemClass(isActive, true)}
                          onClick={handleItemClick}
                          data-testid={`more-dropdown-item-${option.label}`}
                        >
                          {renderItemContent(
                            page.icon,
                            option.label,
                            isActive
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          const isActive = targetPath !== '/' && isPathActive(targetPath);

          if (page.onClick) {
            return (
              <button
                key={page.label}
                type="button"
                className={itemClass(false, true)}
                onClick={() => {
                  page.onClick?.();
                  handleItemClick();
                }}
                data-testid={`more-dropdown-item-${page.label}`}
              >
                {renderItemContent(page.icon, page.label, false)}
              </button>
            );
          }

          return (
            <Link
              key={page.label}
              href={targetPath}
              className={itemClass(isActive)}
              onClick={handleItemClick}
              data-testid={`more-dropdown-item-${page.label}`}
            >
              {renderItemContent(page.icon, page.label, isActive)}
            </Link>
          );
        })}
      </div>
    </div>,
    document.body
  );
};

export default MoreDropdown;
